// Arena HUD (lane C, backlog C-9, C-10, C-11, C-13, C-17, C-18; spec §11, territory-ui.md):
// - top-left: the land % pill over its full-width track (2 decimals, instant), the bag (USD, SOL
//   toggle; silver with a PRACTICE badge in practice), the kill pill "×N", and the best line once
//   a best exists;
// - top-right: the board, top 5 by bag with land %, the own row in slot 7 outside them;
// - bottom-right: the minimap (own land, trail and dot; option: all territory), a red rim while
//   the own ALARM flag is set; on touch it sits above the cash-out hold button;
// - top-centre: the notice banner (one at a time, sliding) and the kill feed (4 lines × 4 s);
// - the one-time first-life tip (spec §11);
// - touch: the cash-out hold button (72 px, `$`, progress ring) where Split sits.
// Geometry follows the UI §1 scale model through CSS custom properties set on the root. DOM is
// touched only when a value changes. The view-models are in hud-model.ts.

import { ARENA_CLIENT, type Rgb } from '@ballio/shared';
import { type Canvas2D, type Ctx2D, get2d, sizeCanvas } from '../render/canvas.js';
import { css, cssAlpha } from '../render/theme.js';
import { t } from '../ui/i18n/t.js';
import {
  type ArenaBoardRow,
  type BannerFrame,
  formatPct,
  hudScale,
  landPillWidth,
} from './hud-model.js';
import { shadesOf, valueOf, withValue } from './shades.js';
import type { ArenaStore } from './store.js';

/** One kill feed line, as spans: plain text, a name in its plate shade, or a money value. */
export interface FeedPart {
  readonly text: string;
  readonly kind: 'text' | 'name' | 'money';
  readonly color?: Rgb;
}

export interface FeedLine {
  readonly parts: readonly FeedPart[];
  readonly own: boolean;
  readonly bornMs: number;
}

export interface ArenaHudModel {
  /** A life is playing (the own block shows). */
  readonly playing: boolean;
  readonly ownColor: Rgb;
  readonly landPct: number;
  readonly bestPct: number | null;
  /** The bag as text ("$12.40"), null outside money and practice rooms. */
  readonly bagText: string | null;
  /** The bag is gone (dead): grey "$0.00". */
  readonly bagLost: boolean;
  readonly practice: boolean;
  readonly kills: number;
  readonly board: readonly ArenaBoardRow[];
  readonly banner: BannerFrame | null;
  /** Touch cash-out button: shown, and its ring 0–1 (null = not holding). */
  readonly holdButton: boolean;
  readonly holdProgress: number | null;
  readonly minimapAll: boolean;
  readonly touch: boolean;
}

const FEED_HOLD_MS = ARENA_CLIENT.killFeedMs;
const FEED_FADE_MS = 300;

function el<K extends keyof HTMLElementTagNameMap>(
  doc: Document,
  tag: K,
  cls: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = doc.createElement(tag);
  e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** Our crossed-blades glyph (kill pill), as inline SVG markup. */
const BLADES_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true" width="100%" height="100%"><path fill="currentColor" d="M4 3l7.2 7.2-1.6 1.6L3 5.2V3h1zm16 0h1v2.2l-6.6 6.6-1.6-1.6L20 3zM8.6 13.4l2 2-3.3 3.3 1.4 1.4-1.4 1.4-2-2-2 2L2 20.1l2-2-2-2 1.4-1.4 1.4 1.4 3.8-2.7zm6.8 0l3.8 2.7 1.4-1.4 1.4 1.4-2 2 2 2-1.4 1.4-2-2-2 2-1.4-1.4 1.4-1.4-3.3-3.3 2.1-2z"/></svg>';

/**
 * A name's colour on the dark feed pill: the main colour, lifted to at least V 80 so dark
 * palette colours stay readable (the plate shade is for text on own-coloured pills).
 */
export function feedNameColor(c: Rgb): Rgb {
  return valueOf(c) >= 80 ? c : withValue(c, 80);
}

export class ArenaHud {
  readonly el: HTMLElement;
  /** The touch cash-out button (the app wires press / release). */
  readonly holdButton: HTMLButtonElement;
  private readonly doc: Document;
  private readonly store: ArenaStore;
  private readonly track: HTMLElement;
  private readonly pill: HTMLElement;
  private readonly pillText: HTMLElement;
  private readonly bag: HTMLElement;
  private readonly bagValue: HTMLElement;
  private readonly badge: HTMLElement;
  private readonly killPill: HTMLElement;
  private readonly killCount: HTMLElement;
  private readonly best: HTMLElement;
  private readonly board: HTMLElement;
  private readonly boardRows: HTMLElement[] = [];
  private readonly minimapWrap: HTMLElement;
  private readonly minimap: HTMLCanvasElement;
  private readonly feed: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly bannerTitle: HTMLElement;
  private readonly bannerDetail: HTMLElement;
  private readonly bannerIcon: HTMLElement;
  private readonly holdRing: HTMLElement;
  private readonly lines: { line: FeedLine; el: HTMLElement }[] = [];
  private last = new Map<string, string>();
  private minimapKey = '';
  private sizeKey = '';

  constructor(doc: Document, store: ArenaStore) {
    this.doc = doc;
    this.store = store;
    const root = el(doc, 'div', 'arena-hud');
    root.hidden = true;
    this.el = root;

    const tl = el(doc, 'div', 'arena-tl');
    this.track = el(doc, 'div', 'arena-track');
    this.pill = el(doc, 'div', 'arena-pill');
    this.pillText = el(doc, 'span', 'arena-pill-text', '0.00%');
    this.pill.append(this.pillText);
    this.track.append(this.pill);
    this.bag = el(doc, 'div', 'arena-bag');
    this.bagValue = el(doc, 'span', 'arena-bag-value');
    this.badge = el(doc, 'span', 'arena-badge', t('practice.badge'));
    this.badge.hidden = true;
    this.bag.append(this.bagValue, this.badge);
    this.killPill = el(doc, 'div', 'arena-kills');
    const glyph = el(doc, 'span', 'arena-kills-glyph');
    glyph.innerHTML = BLADES_SVG;
    this.killCount = el(doc, 'span', 'arena-kills-count', t('arena.hud.kills', { n: 0 }));
    this.killPill.append(glyph, this.killCount);
    this.best = el(doc, 'div', 'arena-best');
    this.best.hidden = true;
    tl.append(this.track, this.bag, this.killPill, this.best);

    this.board = el(doc, 'ol', 'arena-board');
    this.board.setAttribute('aria-label', t('hud.leaderboard'));
    for (let i = 0; i < 7; i++) {
      const row = el(doc, 'li', 'arena-board-row');
      row.hidden = true;
      this.boardRows.push(row);
      this.board.append(row);
    }

    this.minimapWrap = el(doc, 'div', 'arena-minimap');
    this.minimap = el(doc, 'canvas', 'arena-minimap-canvas');
    this.minimap.setAttribute('aria-label', t('arena.hud.minimap'));
    this.minimapWrap.append(this.minimap);

    this.feed = el(doc, 'div', 'arena-feed');
    this.feed.setAttribute('aria-live', 'polite');

    this.banner = el(doc, 'div', 'arena-banner');
    this.bannerIcon = el(doc, 'span', 'arena-banner-icon');
    const text = el(doc, 'span', 'arena-banner-text');
    this.bannerTitle = el(doc, 'span', 'arena-banner-title');
    this.bannerDetail = el(doc, 'span', 'arena-banner-detail');
    text.append(this.bannerTitle, this.bannerDetail);
    this.banner.append(this.bannerIcon, text);
    this.banner.hidden = true;

    this.holdButton = el(doc, 'button', 'arena-hold');
    this.holdButton.type = 'button';
    this.holdButton.setAttribute('aria-label', t('arena.hud.cashOut'));
    this.holdRing = el(doc, 'span', 'arena-hold-ring');
    this.holdButton.append(this.holdRing, el(doc, 'span', 'arena-hold-glyph', '$'));
    this.holdButton.hidden = true;

    root.append(tl, this.board, this.minimapWrap, this.feed, this.banner, this.holdButton);
  }

  setVisible(on: boolean): void {
    if (this.el.hidden === !on) return;
    this.el.hidden = !on;
  }

  /** Adds a kill feed line (newest on top, 4 at most). */
  pushFeed(line: FeedLine): void {
    const row = el(this.doc, 'div', line.own ? 'arena-feed-line is-own' : 'arena-feed-line');
    for (const p of line.parts) {
      const span = el(this.doc, 'span', `arena-feed-${p.kind}`, p.text);
      if (p.kind === 'name' && p.color !== undefined)
        span.style.color = css(feedNameColor(p.color));
      row.append(span);
    }
    this.feed.prepend(row);
    this.lines.unshift({ line, el: row });
    while (this.lines.length > ARENA_CLIENT.killFeedLines) this.lines.pop()?.el.remove();
  }

  clearFeed(): void {
    for (const l of this.lines.splice(0)) l.el.remove();
  }

  private set(key: string, value: string, apply: () => void): void {
    if (this.last.get(key) === value) return;
    this.last.set(key, value);
    apply();
  }

  /** Refreshes the DOM (once per rendered frame; cheap when unchanged). */
  update(m: ArenaHudModel, nowMs: number, widthPx: number, heightPx: number, dpr: number): void {
    const sc = hudScale(widthPx, heightPx, dpr);
    const sizeKey = `${widthPx}x${heightPx}@${dpr}`;
    if (sizeKey !== this.sizeKey) {
      this.sizeKey = sizeKey;
      const st = this.el.style;
      st.setProperty('--aF', `${sc.F.toFixed(2)}px`);
      st.setProperty('--aP', `${sc.P.toFixed(2)}px`);
      st.setProperty('--aL', `${sc.L.toFixed(2)}px`);
      st.setProperty('--aH', `${sc.H.toFixed(2)}px`);
      st.setProperty('--aW', `${sc.W.toFixed(2)}px`);
      st.setProperty('--aM', `${sc.M.toFixed(2)}px`);
      st.setProperty('--aS', sc.s.toFixed(4));
    }
    this.el.classList.toggle('is-touch', m.touch);

    // Top-left.
    const sh = shadesOf(m.ownColor);
    this.set('playing', String(m.playing), () => this.el.classList.toggle('is-playing', m.playing));
    const pillW = landPillWidth(sc.W, m.landPct, m.bestPct);
    this.set('pillW', pillW.toFixed(1), () => (this.pill.style.width = `${pillW.toFixed(1)}px`));
    this.set('pillC', String(m.ownColor), () => {
      this.pill.style.setProperty('--fill', css(sh.main));
      this.pill.style.setProperty('--lip', css(sh.side));
      this.pillText.style.color = css(sh.plate);
    });
    this.set('pct', formatPct(m.landPct), () => (this.pillText.textContent = formatPct(m.landPct)));
    this.set('bag', `${m.bagText ?? ''}|${m.bagLost}|${m.practice}`, () => {
      this.bag.hidden = m.bagText === null;
      this.bagValue.textContent = m.bagLost ? '$0.00' : (m.bagText ?? '');
      this.bag.classList.toggle('is-lost', m.bagLost);
      this.bag.classList.toggle('is-practice', m.practice);
      this.badge.hidden = !m.practice;
    });
    this.set('kills', String(m.kills), () => {
      this.killCount.textContent = t('arena.hud.kills', { n: m.kills });
    });
    this.set('best', String(m.bestPct), () => {
      this.best.hidden = m.bestPct === null || !(m.bestPct > 0);
      if (m.bestPct !== null)
        this.best.textContent = t('arena.hud.best', { pct: formatPct(m.bestPct) });
    });

    // Board.
    const boardKey = m.board
      .map((r) => `${r.slot}:${r.text}:${r.length.toFixed(0)}:${r.isYou}:${r.color ?? ''}`)
      .join('|');
    this.set('board', boardKey, () => {
      for (let i = 0; i < this.boardRows.length; i++) {
        const row = this.boardRows[i] as HTMLElement;
        const r = m.board.find((b) => b.slot === i);
        row.hidden = !r;
        if (!r) continue;
        row.textContent = r.text;
        row.style.setProperty('--len', `${r.length.toFixed(1)}px`);
        row.classList.toggle('is-you', r.isYou);
        // Rows in the player's colour with plate-shade text (visuals §5); neutral when unknown.
        if (r.color !== null) {
          const rs = shadesOf(r.color);
          row.style.setProperty('--row-bg', css(rs.main));
          row.style.setProperty('--row-lip', css(rs.side));
          row.style.setProperty('--row-fg', css(rs.plate));
        } else {
          row.style.removeProperty('--row-bg');
          row.style.removeProperty('--row-lip');
          row.style.removeProperty('--row-fg');
        }
      }
    });

    // Minimap.
    this.drawMinimap(m, sc.M, sc.s, dpr);

    // Banner.
    const b = m.banner;
    this.set('banner', b ? `${b.item.key}|${b.item.title}|${b.item.detail}` : '', () => {
      this.banner.hidden = !b;
      if (!b) return;
      this.bannerTitle.textContent = b.item.title;
      this.bannerDetail.textContent = b.item.detail;
      this.bannerDetail.hidden = b.item.detail === '';
      this.bannerIcon.dataset['icon'] = b.item.icon;
    });
    if (b) this.banner.style.setProperty('--shown', b.shown.toFixed(3));

    // Feed: hold 4 s, then fade 300 ms.
    for (let i = this.lines.length - 1; i >= 0; i--) {
      const l = this.lines[i] as { line: FeedLine; el: HTMLElement };
      const age = nowMs - l.line.bornMs;
      if (age >= FEED_HOLD_MS + FEED_FADE_MS) {
        l.el.remove();
        this.lines.splice(i, 1);
      } else if (age > FEED_HOLD_MS) {
        l.el.style.opacity = (1 - (age - FEED_HOLD_MS) / FEED_FADE_MS).toFixed(3);
      }
    }

    // Touch hold button.
    this.set('hold', String(m.holdButton), () => (this.holdButton.hidden = !m.holdButton));
    const p = m.holdProgress;
    this.set('holdP', p === null ? '' : p.toFixed(3), () => {
      this.holdButton.classList.toggle('is-holding', p !== null);
      this.holdRing.style.setProperty('--p', p === null ? '0' : p.toFixed(3));
    });
  }

  private drawMinimap(m: ArenaHudModel, side: number, s: number, dpr: number): void {
    const st = this.store;
    const own = st.own.playerId;
    const trail = st.trailOf(own);
    const key = [
      side.toFixed(0),
      dpr,
      st.minimap.version,
      st.landVersion,
      own,
      trail.length,
      st.own.alarm,
      m.minimapAll,
      st.playersVersion,
      Math.round((st.own.head?.x ?? 0) * 2),
      Math.round((st.own.head?.y ?? 0) * 2),
    ].join('|');
    if (key === this.minimapKey) return;
    this.minimapKey = key;
    const k = Math.max(1, dpr);
    const px = Math.round(side * k);
    this.minimapWrap.style.width = `${side}px`;
    this.minimapWrap.style.height = `${side}px`;
    sizeCanvas(this.minimap as unknown as Canvas2D, px, px);
    const ctx = get2d(this.minimap as unknown as Canvas2D);
    if (!ctx) return;
    drawArenaMinimap(ctx, st, px, s * k, m.minimapAll);
  }

  destroy(): void {
    this.el.remove();
  }
}

/**
 * The minimap picture (UI §3, visuals §12): the arena at α 0.67, own land (and everyone's with
 * `all`, at α 0.5) from the downsampled ownership map (or the client's own grid when the server
 * sent none yet), own trail, own dot in the base shade, a 3 px × s rim (red on alarm).
 */
export function drawArenaMinimap(
  ctx: Ctx2D,
  st: ArenaStore,
  px: number,
  s: number,
  all: boolean,
): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, px, px);
  const own = st.own.playerId;
  const rim = Math.max(2, 3 * s);
  const r = px / 2 - rim / 2;
  const circle = st.info.shape === 'circle';
  const clip = (): void => {
    ctx.beginPath();
    if (circle) ctx.arc(px / 2, px / 2, r, 0, Math.PI * 2);
    else ctx.rect(rim / 2, rim / 2, px - rim, px - rim);
  };
  ctx.save();
  clip();
  ctx.fillStyle = cssAlpha(ARENA_CLIENT.colors.minimapArena, 0.67);
  ctx.fill();
  ctx.clip();
  const cols = st.cols;
  const rows = st.rows;
  const mm = st.minimap;
  if (mm.size > 0 && mm.cells.length === mm.size * mm.size) {
    const b = px / mm.size;
    for (let y = 0; y < mm.size; y++) {
      for (let x = 0; x < mm.size; x++) {
        const id = mm.cells[y * mm.size + x] as number;
        if (id === 0 || (id !== own && !all)) continue;
        ctx.fillStyle = id === own ? css(st.colorOf(id)) : cssAlpha(st.colorOf(id), 0.5);
        ctx.fillRect(x * b, y * b, b + 0.5, b + 0.5);
      }
    }
  } else {
    // No minimap section yet: the own grid, downsampled to 64 × 64.
    const n = 64;
    const bx = px / n;
    for (let y = 0; y < n; y++) {
      const cy = Math.floor(((y + 0.5) * rows) / n);
      for (let x = 0; x < n; x++) {
        const cx = Math.floor(((x + 0.5) * cols) / n);
        const id = st.ownerAt(cx, cy);
        if (id === 0 || (id !== own && !all)) continue;
        ctx.fillStyle = id === own ? css(st.colorOf(id)) : cssAlpha(st.colorOf(id), 0.5);
        ctx.fillRect(x * bx, y * bx, bx + 0.5, bx + 0.5);
      }
    }
  }
  if (own !== 0) {
    const sh = shadesOf(st.colorOf(own));
    const trail = st.trailOf(own);
    if (trail.length > 1) {
      ctx.strokeStyle = cssAlpha(sh.main, 0.8);
      ctx.lineWidth = Math.max(1, s * 1.5);
      ctx.lineJoin = 'round';
      ctx.beginPath();
      for (let i = 0; i < trail.length; i++) {
        const c = trail[i] as number;
        const x = (((c % cols) + 0.5) / cols) * px;
        const y = ((Math.floor(c / cols) + 0.5) / rows) * px;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    const head = st.own.head;
    if (head) {
      ctx.fillStyle = css(sh.base);
      ctx.beginPath();
      ctx.arc((head.x / cols) * px, (head.y / rows) * px, Math.max(2, 3 * s), 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
  clip();
  ctx.lineWidth = rim;
  ctx.strokeStyle = st.own.alarm ? css(ARENA_CLIENT.colors.minimapAlarm) : cssAlpha(0x000000, 0.6);
  ctx.stroke();
}
